<script setup>
import { onMounted, reactive, ref } from "vue";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Quotation Automation");

const loading = ref(false);
const actionBusy = ref(false);
const settings = ref({});
const upcoming = ref({ data: [], current_page: 1, last_page: 1, total: 0 });
const logs = ref({ data: [], current_page: 1, last_page: 1, total: 0 });
const holidays = ref([]);
const templates = ref([]);
const templateHistory = ref({ data: [], current_page: 1, last_page: 1, total: 0 });
const selected = ref(null);
const editingTemplate = ref(null);
const filters = reactive({ query: "", stage: "", status: "", from: "", to: "" });
const pages = reactive({ upcoming: 1, logs: 1, templates: 1 });
const holidayForm = reactive({ date: "", name: "" });

const stages = [
  ["quote_check", "Quote Check"],
  ["nudge_soft", "Nudge (Soft)"],
  ["nudge_final", "Nudge (Final)"],
  ["close_out", "Close Out"],
  ["acceptance_confirmation", "Acceptance Confirmation"],
  ["client_details_check", "Client Details Check"],
  ["relationship_check_in", "Relationship Check-In"],
  ["short_notice_confirmation", "Short-Notice Confirmation"],
  ["post_event_follow_up", "Post-Event Follow-Up"],
  ["renewal_follow_up", "Renewal Follow-Up"],
  ["requote_follow_up", "Requote Follow-Up"],
];

const stageLabel = (value) => stages.find(([key]) => key === value)?.[1] ?? value?.replaceAll("_", " ") ?? "Automation";
const formatDate = (value) => value
  ? new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/London" }).format(new Date(value))
  : "-";
const statusClass = (status) => ({
  sent: "bg-emerald-100 text-emerald-800",
  scheduled: "bg-sky-100 text-sky-800",
  blocked: "bg-amber-100 text-amber-800",
  failed: "bg-rose-100 text-rose-800",
  paused: "bg-violet-100 text-violet-800",
}[status] ?? "bg-slate-100 text-slate-700");

const params = () => ({
  per_page: 25,
  upcoming_page: pages.upcoming,
  logs_page: pages.logs,
  templates_page: pages.templates,
  ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
});

const loadAutomation = async () => {
  loading.value = true;
  try {
    const { data } = await apiClient.get("/api/super/quote-automation", { params: params() });
    settings.value = data.settings ?? {};
    upcoming.value = data.upcoming ?? upcoming.value;
    logs.value = data.logs ?? logs.value;
    templates.value = data.templates ?? [];
    templateHistory.value = data.template_history ?? templateHistory.value;
    pages.upcoming = upcoming.value.current_page ?? 1;
    pages.logs = logs.value.current_page ?? 1;
    pages.templates = templateHistory.value.current_page ?? 1;
  } finally {
    loading.value = false;
  }
};

const loadHolidays = async () => {
  const year = new Date().getFullYear();
  const { data } = await apiClient.get("/api/super/business-holidays", {
    params: { from: `${year}-01-01`, to: `${year + 2}-12-31` },
  });
  holidays.value = data ?? [];
};

const refresh = async () => Promise.all([loadAutomation(), loadHolidays()]);
const applyFilters = () => {
  pages.upcoming = 1;
  pages.logs = 1;
  loadAutomation();
};
const changePage = (kind, delta) => {
  pages[kind] = Math.max(1, pages[kind] + delta);
  loadAutomation();
};

const cycleAction = async (cycleId, action) => {
  actionBusy.value = true;
  try {
    await apiClient.post(`/api/super/quote-automation/cycles/${cycleId}/${action}`);
    await loadAutomation();
  } finally {
    actionBusy.value = false;
  }
};
const retryDelivery = async (deliveryId) => {
  actionBusy.value = true;
  try {
    await apiClient.post(`/api/super/quote-automation/deliveries/${deliveryId}/retry`);
    await loadAutomation();
  } finally {
    actionBusy.value = false;
  }
};

const saveHoliday = async () => {
  if (!holidayForm.date || !holidayForm.name.trim()) return;
  actionBusy.value = true;
  try {
    await apiClient.post("/api/super/business-holidays", {
      region: "england-and-wales",
      date: holidayForm.date,
      name: holidayForm.name.trim(),
      is_active: true,
    });
    holidayForm.date = "";
    holidayForm.name = "";
    await refresh();
  } finally {
    actionBusy.value = false;
  }
};
const toggleHoliday = async (holiday) => {
  await apiClient.put(`/api/super/business-holidays/${holiday.id}`, { is_active: !holiday.is_active });
  await refresh();
};
const deleteHoliday = async (holiday) => {
  if (!confirm(`Delete ${holiday.name}?`)) return;
  await apiClient.delete(`/api/super/business-holidays/${holiday.id}`);
  await refresh();
};
const syncHolidays = async () => {
  actionBusy.value = true;
  try {
    await apiClient.post("/api/super/business-holidays/sync");
    await refresh();
  } finally {
    actionBusy.value = false;
  }
};

const editTemplate = (template) => {
  editingTemplate.value = {
    ...template,
    subject: template.subject ?? "",
    body: template.body ?? "",
  };
};
const saveTemplate = async () => {
  if (!editingTemplate.value) return;
  actionBusy.value = true;
  try {
    await apiClient.put(`/api/super/quote-automation/templates/${editingTemplate.value.stage}`, {
      subject: editingTemplate.value.subject,
      body: editingTemplate.value.body,
    });
    editingTemplate.value = null;
    await loadAutomation();
  } catch (error) {
    const errors = error?.response?.data?.errors;
    alert(errors ? Object.values(errors).flat().join("\n") : "Unable to save the email template.");
  } finally {
    actionBusy.value = false;
  }
};
const resetTemplate = async (template) => {
  if (!confirm(`Reset ${template.label} to the code default?`)) return;
  actionBusy.value = true;
  try {
    await apiClient.delete(`/api/super/quote-automation/templates/${template.stage}`);
    editingTemplate.value = null;
    await loadAutomation();
  } finally {
    actionBusy.value = false;
  }
};

onMounted(refresh);
</script>

<template>
  <div class="space-y-6">
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.25em] text-slate-400">Super User</p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">Quotation Automation</h1>
        <p class="mt-2 text-sm text-slate-500 dark:text-slate-300">
          Upcoming quotation emails, the complete automation audit trail, and the shared business calendar.
        </p>
      </div>
      <div class="flex flex-wrap gap-2 text-xs">
        <span class="rounded-full px-3 py-1" :class="settings.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'">
          Scheduling {{ settings.enabled ? "enabled" : "disabled" }}
        </span>
        <span class="rounded-full px-3 py-1" :class="settings.sending_enabled ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'">
          Sending {{ settings.sending_enabled ? "enabled" : "dry-run only" }}
        </span>
      </div>
    </header>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <h2 class="text-lg font-semibold">Email Templates</h2>
        <p class="text-sm text-slate-500">Edit the quote nudges and event follow-ups. Changes affect future sends, including messages already scheduled but not yet sent.</p>
      </div>
      <div class="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
        <article v-for="template in templates" :key="template.stage" class="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="font-semibold">{{ template.label }}</h3>
              <span class="mt-1 inline-block rounded-full px-2 py-0.5 text-xs" :class="template.is_custom ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-600'">
                {{ template.is_custom ? "Custom" : "Default" }}
              </span>
            </div>
            <button class="btn text-xs" :disabled="actionBusy" @click="editTemplate(template)">Edit</button>
          </div>
          <p class="mt-3 text-sm font-medium text-slate-800 dark:text-slate-100">{{ template.subject }}</p>
          <p class="mt-2 line-clamp-3 whitespace-pre-wrap text-xs leading-5 text-slate-500">{{ template.body }}</p>
          <p v-if="template.updated_by" class="mt-3 text-xs text-slate-400">Updated by {{ template.updated_by.name }} · {{ formatDate(template.updated_at) }}</p>
        </article>
      </div>
      <details v-if="templateHistory.total" class="border-t border-slate-200 px-5 py-4 dark:border-slate-700">
        <summary class="cursor-pointer text-sm font-semibold">Template change history ({{ templateHistory.total }})</summary>
        <div class="mt-4 overflow-x-auto">
          <table class="w-full min-w-[720px] text-left text-sm">
            <thead class="text-xs uppercase text-slate-500"><tr><th class="py-2 pr-4">Time</th><th class="py-2 pr-4">Template</th><th class="py-2 pr-4">Action</th><th class="py-2 pr-4">User</th><th class="py-2">New subject</th></tr></thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
              <tr v-for="revision in templateHistory.data" :key="revision.id"><td class="py-3 pr-4">{{ formatDate(revision.occurred_at) }}</td><td class="py-3 pr-4">{{ stageLabel(revision.stage) }}</td><td class="py-3 pr-4">{{ revision.action }}</td><td class="py-3 pr-4">{{ revision.actor?.name || "System" }}</td><td class="py-3">{{ revision.new_subject }}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 flex items-center justify-between text-sm"><span>Page {{ templateHistory.current_page }} of {{ templateHistory.last_page }}</span><div class="flex gap-2"><button class="btn" :disabled="templateHistory.current_page <= 1" @click="changePage('templates', -1)">Previous</button><button class="btn" :disabled="templateHistory.current_page >= templateHistory.last_page" @click="changePage('templates', 1)">Next</button></div></div>
      </details>
    </section>

    <form class="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:grid-cols-6" @submit.prevent="applyFilters">
      <input v-model="filters.query" class="input md:col-span-2" placeholder="Quote, customer, recipient..." />
      <select v-model="filters.stage" class="input">
        <option value="">All stages</option>
        <option v-for="stage in stages" :key="stage[0]" :value="stage[0]">{{ stage[1] }}</option>
      </select>
      <select v-model="filters.status" class="input">
        <option value="">All statuses</option>
        <option value="scheduled">Scheduled</option>
        <option value="blocked">Blocked</option>
        <option value="failed">Failed</option>
      </select>
      <input v-model="filters.from" type="date" class="input" aria-label="From date" />
      <button class="btn accent" type="submit">Apply filters</button>
    </form>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <h2 class="text-lg font-semibold">Upcoming Schedule</h2>
        <p class="text-sm text-slate-500">Pending, blocked and failed deliveries. Select a row to preview the exact email.</p>
      </div>
      <div v-if="loading" class="p-5 text-sm text-slate-500">Loading automation...</div>
      <div v-else-if="!upcoming.data?.length" class="p-5 text-sm text-slate-500">No matching deliveries.</div>
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[1000px] text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
            <tr><th class="px-5 py-3">Scheduled</th><th class="px-5 py-3">Stage</th><th class="px-5 py-3">Quote</th><th class="px-5 py-3">Recipient</th><th class="px-5 py-3">Status</th><th class="px-5 py-3">Controls</th></tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr v-for="item in upcoming.data" :key="item.id" class="cursor-pointer hover:bg-cyan-50/60 dark:hover:bg-cyan-950/20" @click="selected = item">
              <td class="px-5 py-4">{{ formatDate(item.scheduled_for) }}</td>
              <td class="px-5 py-4"><div class="font-medium">{{ stageLabel(item.stage) }}</div><div class="text-xs text-slate-500">{{ item.cadence }}</div></td>
              <td class="px-5 py-4">
                <router-link v-if="item.quote?.quote_url" :to="item.quote.quote_url" class="font-medium text-cyan-700" @click.stop>{{ item.quote.reference || `Quote #${item.quote.id}` }}</router-link>
                <div class="max-w-sm truncate text-slate-500">{{ item.quote?.description }}</div>
              </td>
              <td class="px-5 py-4"><div>{{ item.recipient?.name }}</div><div class="text-xs text-slate-500">{{ item.recipient?.email }}</div></td>
              <td class="px-5 py-4"><span class="rounded-full px-2 py-1 text-xs font-medium" :class="statusClass(item.status)">{{ item.status }}</span><div v-if="item.reason" class="mt-1 text-xs text-amber-700">{{ item.reason }}</div></td>
              <td class="px-5 py-4" @click.stop>
                <div class="flex flex-wrap gap-2">
                  <button v-if="item.status === 'failed' || item.status === 'blocked'" class="btn text-xs" :disabled="actionBusy" @click="retryDelivery(item.id)">Retry</button>
                  <button v-if="item.cycle_status === 'active'" class="btn text-xs" :disabled="actionBusy" @click="cycleAction(item.cycle_id, 'pause')">Pause</button>
                  <button v-if="item.cycle_status === 'paused'" class="btn text-xs" :disabled="actionBusy" @click="cycleAction(item.cycle_id, 'resume')">Resume</button>
                  <button class="btn text-xs text-rose-600" :disabled="actionBusy" @click="cycleAction(item.cycle_id, 'cancel')">Cancel</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-700">
        <span>Page {{ upcoming.current_page }} of {{ upcoming.last_page }} · {{ upcoming.total }} entries</span>
        <div class="flex gap-2"><button class="btn" :disabled="upcoming.current_page <= 1" @click="changePage('upcoming', -1)">Previous</button><button class="btn" :disabled="upcoming.current_page >= upcoming.last_page" @click="changePage('upcoming', 1)">Next</button></div>
      </div>
    </section>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="border-b border-slate-200 px-5 py-4 dark:border-slate-700"><h2 class="text-lg font-semibold">Full Audit Trail</h2><p class="text-sm text-slate-500">Append-only scheduling, pause, send, delivery, failure and close-out events.</p></div>
      <div v-if="!logs.data?.length" class="p-5 text-sm text-slate-500">No matching audit events.</div>
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[900px] text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40"><tr><th class="px-5 py-3">Time</th><th class="px-5 py-3">Event</th><th class="px-5 py-3">Quote</th><th class="px-5 py-3">Stage</th><th class="px-5 py-3">Actor</th></tr></thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr v-for="item in logs.data" :key="item.id" class="cursor-pointer hover:bg-cyan-50/60 dark:hover:bg-cyan-950/20" @click="selected = item">
              <td class="px-5 py-4">{{ formatDate(item.occurred_at) }}</td><td class="px-5 py-4"><div class="font-medium">{{ item.summary }}</div><div class="text-xs text-slate-500">{{ item.event }}</div></td><td class="px-5 py-4">{{ item.quote?.reference || item.quote?.description }}</td><td class="px-5 py-4">{{ stageLabel(item.stage) }}</td><td class="px-5 py-4">{{ item.actor?.name || "System" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-700"><span>Page {{ logs.current_page }} of {{ logs.last_page }} · {{ logs.total }} events</span><div class="flex gap-2"><button class="btn" :disabled="logs.current_page <= 1" @click="changePage('logs', -1)">Previous</button><button class="btn" :disabled="logs.current_page >= logs.last_page" @click="changePage('logs', 1)">Next</button></div></div>
    </section>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700"><div><h2 class="text-lg font-semibold">Business Calendar</h2><p class="text-sm text-slate-500">England and Wales non-working dates shared by quotation scheduling and future pricing.</p></div><button class="btn accent" :disabled="actionBusy" @click="syncHolidays">Sync GOV.UK</button></div>
      <form class="grid gap-3 border-b border-slate-200 p-5 md:grid-cols-[180px_1fr_auto] dark:border-slate-700" @submit.prevent="saveHoliday"><input v-model="holidayForm.date" type="date" class="input" required /><input v-model="holidayForm.name" class="input" placeholder="Holiday name" required /><button class="btn" :disabled="actionBusy">Add holiday</button></form>
      <div class="divide-y divide-slate-200 dark:divide-slate-700">
        <div v-for="holiday in holidays" :key="holiday.id" class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm"><div><span class="font-medium">{{ holiday.date?.slice(0, 10) }} · {{ holiday.name }}</span><span class="ml-2 text-xs text-slate-400">{{ holiday.source }}</span></div><div class="flex gap-2"><button class="btn text-xs" @click="toggleHoliday(holiday)">{{ holiday.is_active ? "Disable" : "Enable" }}</button><button class="btn text-xs text-rose-600" @click="deleteHoliday(holiday)">Delete</button></div></div>
      </div>
    </section>

    <div v-if="selected" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4" @click.self="selected = null">
      <div class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div class="flex items-start justify-between gap-4"><div><p class="text-xs uppercase tracking-[0.2em] text-cyan-500">Automation Detail</p><h2 class="mt-1 text-xl font-semibold">{{ stageLabel(selected.stage) }}</h2></div><button class="btn" @click="selected = null">Close</button></div>
        <template v-if="selected.email">
          <div class="mt-5 rounded-lg border border-slate-200 p-4 dark:border-slate-700"><div class="text-xs uppercase tracking-wide text-slate-500">Subject</div><p class="mt-2 font-medium">{{ selected.email.subject }}</p></div>
          <div class="mt-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700"><div class="text-xs uppercase tracking-wide text-slate-500">Body</div><p class="mt-2 whitespace-pre-wrap text-sm leading-6">{{ selected.email.body }}</p></div>
        </template>
        <div v-if="selected.metadata" class="mt-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700"><div class="text-xs uppercase tracking-wide text-slate-500">Audit Metadata</div><pre class="mt-2 overflow-x-auto whitespace-pre-wrap text-xs">{{ JSON.stringify(selected.metadata, null, 2) }}</pre></div>
        <p v-if="selected.error" class="mt-4 rounded-lg bg-rose-50 p-4 text-sm text-rose-700">{{ selected.error }}</p>
      </div>
    </div>

    <div v-if="editingTemplate" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4" @click.self="editingTemplate = null">
      <form class="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-900" @submit.prevent="saveTemplate">
        <div class="flex items-start justify-between gap-4">
          <div><p class="text-xs uppercase tracking-[0.2em] text-cyan-500">Email Template</p><h2 class="mt-1 text-xl font-semibold">{{ editingTemplate.label }}</h2></div>
          <button type="button" class="btn" @click="editingTemplate = null">Close</button>
        </div>
        <label class="mt-5 block text-sm font-semibold">Subject</label>
        <input v-model="editingTemplate.subject" class="input mt-2 w-full" maxlength="500" required />
        <label class="mt-5 block text-sm font-semibold">Message body</label>
        <textarea v-model="editingTemplate.body" class="input mt-2 min-h-[340px] w-full whitespace-pre-wrap font-mono text-sm" maxlength="20000" required></textarea>
        <div class="mt-4 rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Available placeholders</p>
          <div class="mt-2 flex flex-wrap gap-2"><code v-for="placeholder in editingTemplate.allowed_placeholders" :key="placeholder" class="rounded bg-white px-2 py-1 text-xs dark:bg-slate-700">{{ "{" + placeholder + "}" }}</code></div>
          <p class="mt-2 text-xs text-slate-500">Placeholders are replaced when the message is sent. Unsupported placeholders will be rejected.</p>
        </div>
        <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button v-if="editingTemplate.is_custom" type="button" class="btn text-rose-600" :disabled="actionBusy" @click="resetTemplate(editingTemplate)">Reset to default</button>
          <span v-else></span>
          <div class="flex gap-2"><button type="button" class="btn" @click="editingTemplate = null">Cancel</button><button type="submit" class="btn accent" :disabled="actionBusy">Save template</button></div>
        </div>
      </form>
    </div>
  </div>
</template>
